import type { AssignmentQuestion } from "../content/assignments";

type Value = string | number | boolean | null;

const TYPE_LABEL: Record<AssignmentQuestion["type"], string> = {
  multiple_choice: "Multiple choice",
  true_false: "True / False",
  numeric: "Calculation",
  free_response: "Short answer",
};

// Renders one assignment question by its type:
//   multiple_choice → radios · true_false → True/False radios ·
//   numeric → number input with unit + tolerance hint · free_response → textarea
export default function QuestionField({
  question,
  index,
  value,
  disabled,
  onChange,
}: {
  question: AssignmentQuestion;
  index: number;
  value: Value;
  disabled?: boolean;
  onChange: (v: Value) => void;
}) {
  return (
    <fieldset className="rounded-lg border border-slate-200 bg-white p-5">
      <div className="mb-2 flex items-center gap-2">
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
          {index + 1}
        </span>
        <span className="rounded bg-sky-50 px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-sky-700">
          {TYPE_LABEL[question.type]}
        </span>
      </div>
      <legend className="sr-only">{`Question ${index + 1}`}</legend>
      <p className="font-medium text-slate-900">{question.prompt}</p>
      {question.help && (
        <p className="mt-1 text-sm text-slate-500">{question.help}</p>
      )}

      <div className="mt-3">
        {question.type === "multiple_choice" && (
          <div className="space-y-2">
            {(question.choices ?? []).map((choice) => (
              <label
                key={choice}
                className="flex cursor-pointer items-center gap-3 rounded border border-slate-200 px-3 py-2 hover:bg-slate-50"
              >
                <input
                  type="radio"
                  name={question.id}
                  value={choice}
                  checked={value === choice}
                  disabled={disabled}
                  onChange={() => onChange(choice)}
                  className="h-4 w-4"
                />
                <span className="text-slate-700">{choice}</span>
              </label>
            ))}
          </div>
        )}

        {question.type === "true_false" && (
          <div className="flex gap-3">
            {[
              { label: "True", val: true },
              { label: "False", val: false },
            ].map((opt) => (
              <label
                key={opt.label}
                className="flex cursor-pointer items-center gap-2 rounded border border-slate-200 px-4 py-2 hover:bg-slate-50"
              >
                <input
                  type="radio"
                  name={question.id}
                  checked={value === opt.val}
                  disabled={disabled}
                  onChange={() => onChange(opt.val)}
                  className="h-4 w-4"
                />
                <span className="text-slate-700">{opt.label}</span>
              </label>
            ))}
          </div>
        )}

        {question.type === "numeric" && (
          <div className="flex items-center gap-2">
            <input
              type="number"
              inputMode="decimal"
              step="any"
              value={value === null || value === undefined ? "" : String(value)}
              disabled={disabled}
              onChange={(e) =>
                onChange(e.target.value === "" ? null : Number(e.target.value))
              }
              className="w-40 rounded border border-slate-300 px-3 py-2 text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 disabled:bg-slate-50"
              placeholder="0"
            />
            {question.unit && (
              <span className="text-sm text-slate-500">{question.unit}</span>
            )}
            {typeof question.tolerance === "number" && (
              <span className="text-xs text-slate-400">
                (± {question.tolerance})
              </span>
            )}
          </div>
        )}

        {question.type === "free_response" && (
          <textarea
            rows={5}
            value={value === null || value === undefined ? "" : String(value)}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2 text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 disabled:bg-slate-50"
            placeholder="Type your answer…"
          />
        )}
      </div>
    </fieldset>
  );
}
